import { useContext, useEffect, useState } from 'react'

import { faCog } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Card, CardContent, CardHeader, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ContentItemModal } from '@scoir/college-content'

import ContentManagementContext from '../../ContentManagementProvider/Context'
import ContentModal from '../ContentModal'
import ContentReorderModal from '../ContentReorderModal'
import NewContentMenuButton from '../NewContentMenuButton'
import PublishedStateToggle from '../PublishedStateToggle'
import TopInterests from '../TopInterests'
import ContentTable from './ContentTable'
import ContentTableEmptyState from './ContentTableEmptyState'

type ContentManagementTableProps = {
  canManageContent: boolean
}

const useStyles = makeStyles((theme) => ({
  cardHeaderRoot: {
    flexWrap: 'wrap',
    gridGap: theme.spacing(1),
  },
}))
const ContentManagementTable = ({ canManageContent }: ContentManagementTableProps) => {
  const styles = useStyles()
  const [manageOrderOpen, setManageOrderOpen] = useState(false)
  const {
    focusedContentItem,
    setFocusedContentItem,
    content,
    fetchContent,
    fetchContentLoading,
    saveContentSucceeded,
    filterByStatus,
    filterByType,
    filterByTopics,
    order,
    orderBy,
    pageSize,
    from,
    modalMode,
  } = useContext(ContentManagementContext)

  useEffect(() => {
    fetchContent()
  }, [fetchContent, pageSize, from, order, orderBy, filterByStatus, filterByType, filterByTopics])

  useEffect(() => {
    if (saveContentSucceeded) {
      fetchContent()
    }
  }, [fetchContent, saveContentSucceeded])

  const handleReorderModalClose = () => {
    setManageOrderOpen(false)
  }

  const isFiltered = Boolean(
    filterByStatus !== undefined ||
      filterByType?.length ||
      filterByTopics?.AcademicFocus?.length ||
      filterByTopics?.Categories?.length,
  )

  if (fetchContentLoading || !content) return null

  return (
    <Card elevation={0}>
      <CardHeader
        classes={{ root: styles.cardHeaderRoot }}
        title="Content"
        titleTypographyProps={{ variant: 'h6', component: 'h2' }}
        action={
          (content.total || isFiltered) && canManageContent ? (
            <Box
              mt={1}
              mr={1}
              display="flex"
              gridGap={16}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <PublishedStateToggle />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setManageOrderOpen(true)
                }}
                startIcon={<FontAwesomeIcon icon={faCog} />}
              >
                Reorder Display
              </Button>
              <NewContentMenuButton />
            </Box>
          ) : null
        }
      />
      <TopInterests />
      <Divider />
      {!content.total && !isFiltered ? (
        <CardContent>
          <ContentTableEmptyState canManageContent={canManageContent} />
        </CardContent>
      ) : (
        <ContentTable
          content={content}
          viewOnly={!canManageContent}
          hasUnfilteredContent={Boolean(content.total)}
        />
      )}
      {canManageContent
        ? focusedContentItem && (
            <ContentModal
              show={Boolean(focusedContentItem)}
              onHide={() => setFocusedContentItem(undefined)}
              contentItem={focusedContentItem}
              type={focusedContentItem.ContentType}
              mode={modalMode}
            />
          )
        : focusedContentItem && (
            <ContentItemModal
              hostedRootPath={import.meta.env.BASE_URL}
              openItemIdx={0}
              itemsCount={1}
              item={focusedContentItem}
              onChange={() => setFocusedContentItem(undefined)}
              type={focusedContentItem.ContentType}
            />
          )}
      <ContentReorderModal
        isOpen={manageOrderOpen}
        onClose={handleReorderModalClose}
      />
    </Card>
  )
}

export default ContentManagementTable
